import React from 'react';
import { Box, Typography, Tooltip, useTheme } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

/**
 * Component for highlighting code values with optional descriptions
 */
const CodeHighlight = ({ 
  code, 
  description = null, 
  tooltip = null, 
  type = 'default',
  fontSize = 'inherit'
}) => {
  const theme = useTheme();
  
  // Different style variants based on the type
  const getVariantStyles = (theme) => {
    const isDark = theme.palette.mode === 'dark';
    
    switch (type) {
      case 'segment':
        return {
          backgroundColor: isDark ? 'rgba(33, 150, 243, 0.15)' : '#e3f2fd',
          color: isDark ? '#90caf9' : '#0277bd',
          border: `1px solid ${isDark ? 'rgba(33, 150, 243, 0.3)' : '#bbdefb'}`
        };
      case 'element':
        return {
          backgroundColor: isDark ? 'rgba(76, 175, 80, 0.15)' : '#e8f5e9',
          color: isDark ? '#a5d6a7' : '#2e7d32',
          border: `1px solid ${isDark ? 'rgba(76, 175, 80, 0.3)' : '#c8e6c9'}`
        };
      case 'qualifier':
        return {
          backgroundColor: isDark ? 'rgba(255, 152, 0, 0.15)' : '#fff3e0',
          color: isDark ? '#ffb74d' : '#e65100',
          border: `1px solid ${isDark ? 'rgba(255, 152, 0, 0.3)' : '#ffe0b2'}`
        };
      case 'value':
        return {
          backgroundColor: isDark ? 'rgba(100, 116, 139, 0.15)' : '#f5f5f5',
          color: isDark ? '#cbd5e1' : '#424242',
          border: `1px solid ${isDark ? 'rgba(100, 116, 139, 0.3)' : '#e0e0e0'}`
        };
      default:
        return {
          backgroundColor: isDark ? 'rgba(100, 116, 139, 0.15)' : '#f5f5f5',
          color: isDark ? '#cbd5e1' : '#424242',
          border: `1px solid ${isDark ? 'rgba(100, 116, 139, 0.3)' : '#e0e0e0'}`
        };
    }
  };

  const styles = getVariantStyles(theme);

  const codeElement = (
    <Box 
      component="span"
      sx={{
        fontFamily: 'monospace',
        fontSize: fontSize,
        fontWeight: 500,
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.5,
        borderRadius: 1,
        ...styles,
        ...(tooltip && { cursor: 'help' })
      }}
    >
      {code}
      {tooltip && (
        <HelpOutlineIcon 
          sx={{ 
            ml: 0.5, 
            fontSize: '0.9rem',
            opacity: 0.7
          }} 
        />
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'inline-block' }}>
      {tooltip ? (
        <Tooltip 
          title={tooltip} 
          arrow
          placement="top"
        >
          {codeElement}
        </Tooltip>
      ) : (
        codeElement
      )}
      
      {description && (
        <Typography 
          component="span" 
          variant="body2" 
          sx={{ 
            ml: 1,
            color: 'text.secondary'
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
};

export default CodeHighlight; 